import React, { useState } from 'react';
import { Container, Typography, Grid, Box, Button, Fade, Paper, Accordion, AccordionSummary, AccordionDetails, Chip, useMediaQuery } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useTenantTheme } from '../tenant/TenantThemeProvider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

// Función para formatear las URLs de visualización
const formatVisualizationUrl = (url) => {
  if (!url) return null;
  
  // Si ya es una URL completa con http o data, devolverla como está
  if (url.startsWith('data:') || url.startsWith('http')) {
    return url;
  }
  
  // Si es un fragmento de URL que comienza con /9j, es un base64 sin el prefijo
  if (url.startsWith('/9j')) {
    return `data:image/jpeg;base64,${url}`;
  }
  
  // Si es un path parcial que comienza con /, convertirlo en URL completa a la API
  if (url.startsWith('/')) {
    // Usamos el dominio de la API desde el entorno
    const apiBaseUrl = process.env.REACT_APP_API_BASE_URL;
    return `${apiBaseUrl}${url}`;
  }
  
  // Para cualquier otro caso, asumimos que es un base64 sin formato
  return `data:image/jpeg;base64,${url}`;
};

const DanosDetalle = ({ danos, onBack, resultados, onResetInspeccion }) => {
  const [expandedImage, setExpandedImage] = useState(null);
  const history = useHistory();
  const { brandColors } = useTenantTheme();
  const isMobile = useMediaQuery('(max-width:900px)');

  // Definir fuentes de imágenes
  const fuentesImagenes = {
    0: 'Foto Frontal',
    1: 'Foto Trasera',
    2: 'Foto Lateral Izquierda',
    3: 'Foto Lateral Derecha',
    4: 'Foto Motor',
    5: 'Foto Volante',
    6: 'Foto Panel',
    7: 'Foto Odómetro',
    8: 'Foto Rueda',
    9: 'Foto Chasis',
    10: 'Foto Documento'
  };
  
  // Ya no necesitamos gestionar el odómetro aquí, se muestra en AnalisisGeneral.js
  // Organizar daños por tipo de foto
  const danosPorFoto = {};
  
  console.log('Procesando daños recibidos:', danos);
  
  // Inicializar la estructura
  danos.forEach(item => {
    const fotoTipo = fuentesImagenes[item.indice] || `Foto ${item.indice + 1}`;
    console.log(`Inicializando ${fotoTipo}:`, item);
    
    // Solo inicializar la estructura base, no procesar partes o daños aún
    if (!danosPorFoto[fotoTipo]) {
      danosPorFoto[fotoTipo] = {
        danos: [],
        visualizacion: item.visualizacion,
        indice: item.indice,
        patente: null,
        partes: [],
        marcaDetectada: null,
        // Añadir la respuesta completa API para acceder a más datos si es necesario
        apiResponse: item.apiResponse
      };
    }
  });
  
  // Procesar visualizaciones
  danos.forEach(item => {
    const fotoTipo = fuentesImagenes[item.indice] || `Foto ${item.indice + 1}`;
    // Guardar la visualización si existe
    if (item.visualizacion && !danosPorFoto[fotoTipo].visualizacion) {
      danosPorFoto[fotoTipo].visualizacion = item.visualizacion;
    }
  });
  
  // Procesar nuevamente cada item para guardar visualización
  danos.forEach(item => {
    const fotoTipo = fuentesImagenes[item.indice] || `Foto ${item.indice + 1}`;
    
    // Guardar la visualización si existe
    if (item.visualizacion && !danosPorFoto[fotoTipo].visualizacion) {
      danosPorFoto[fotoTipo].visualizacion = item.visualizacion;
    }
  });
  
  // Procesar respuesta completa de la API para todas las fotos
  danos.forEach(item => {
    if (item && item.apiResponse) {
      const apiResp = item.apiResponse;
      console.log('Procesando respuesta API para cada foto en DamageDetalle.js');
      
      // Extraer toda la información de la respuesta
      const result = apiResp.result || {};
      const useDetectionResult = !!result.detection_result;
      
      // Obtener la estructura correcta dependiendo del formato
      const detectionData = useDetectionResult ? 
        result.detection_result || {} : 
        result || {};
      
      // Inicializar índice de foto si no está presente
      const indice = apiResp.indice !== undefined ? apiResp.indice : (item.indice !== undefined ? item.indice : 0);
      const fotoTipo = fuentesImagenes[indice] || `Foto ${indice + 1}`;
      
      // Solo procesar si existe la estructura para este tipo de foto
      if (danosPorFoto[fotoTipo]) {
        console.log(`Procesando datos API para foto tipo: ${fotoTipo}, índice: ${indice}`);
        
        // Extraer patente si existe (ya sea en ocr_data o directamente en la respuesta)
        const licensePlate = detectionData.ocr_data?.license_plate || detectionData.license_plate;
    
        // Extraer marca si existe
        let marcaDetectada = null;
        if (detectionData.vehicle_identifiers && Array.isArray(detectionData.vehicle_identifiers)) {
          const marcaItem = detectionData.vehicle_identifiers.find(item => 
            item.class && (item.class.includes('-marca') || item.class.includes('marca')));
          if (marcaItem) {
            marcaDetectada = marcaItem.class.replace('-marca', '');
            console.log(`Marca detectada para ${fotoTipo}:`, marcaDetectada);
          }
        }
        // También buscar en predictions
        if (!marcaDetectada && detectionData.predictions && Array.isArray(detectionData.predictions)) {
          const marcaItem = detectionData.predictions.find(item => 
            item.class && (item.class.includes('-marca') || item.class.includes('marca')));
          if (marcaItem) {
            marcaDetectada = marcaItem.class.replace('-marca', '');
            console.log(`Marca detectada desde predictions para ${fotoTipo}:`, marcaDetectada);
          }
        }

        // Actualizar datos existentes
        if (licensePlate) {
          danosPorFoto[fotoTipo].patente = licensePlate;
          console.log(`Actualizando patente para ${fotoTipo}:`, licensePlate);
        }
        if (marcaDetectada) {
          danosPorFoto[fotoTipo].marcaDetectada = marcaDetectada;
          console.log(`Actualizando marca para ${fotoTipo}:`, marcaDetectada);
        }
      
        // Procesar part_damages para esta foto
        const partDamages = useDetectionResult ? 
          result.detection_result?.part_damages || [] : 
          result.part_damages || [];
      
        if (partDamages && partDamages.length > 0) {
          console.log(`Procesando ${partDamages.length} partes dañadas para ${fotoTipo}`);
          
          // Limpiar partes existentes antes de agregar nuevas para evitar duplicados
          danosPorFoto[fotoTipo].partes = [];
          
          partDamages.forEach((partDamage) => {
            if (partDamage.part && partDamage.associated_damages) {
              // Añadir la parte a la colección si no existe ya
              const partClass = partDamage.part.class;
              const parteExistente = danosPorFoto[fotoTipo].partes.find(p => 
                p.part && p.part.class === partClass
              );
              
              if (!parteExistente) {
                danosPorFoto[fotoTipo].partes.push({
                  part: partDamage.part,
                  associated_damages: partDamage.associated_damages || []
                });
              }
              
              // Añadir los daños asociados
              if (partDamage.associated_damages && partDamage.associated_damages.length > 0) {
                // Buscar o crear el array de daños para esta foto
                if (!danosPorFoto[fotoTipo].danos) danosPorFoto[fotoTipo].danos = [];
                
                partDamage.associated_damages.forEach(dano => {
                  if (dano.confidence <= 0.1) return; // Ignorar muy baja confianza
                  
                  // Verificar que no esté duplicado
                  const danoExistente = danosPorFoto[fotoTipo].danos.find(d => 
                    d.class === dano.class && d.partId === partDamage.part.class
                  );
                  
                  if (!danoExistente) {
                    danosPorFoto[fotoTipo].danos.push({
                      class: dano.class,
                      confidence: dano.confidence,
                      class_id: dano.class_id || 0,
                      partId: partDamage.part.class
                    });
                  }
                });
              }
            }
          });
        }
      }
    }
  });

const ResultBox = ({ title, children, sx = {} }) => {
    const { brandColors } = useTenantTheme();
    return (
      <Box 
        className="result-box" 
        p={3} 
        borderRadius={2} 
        sx={{
          backgroundColor: brandColors.cardBackground || '#ffffff',
          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.08)',
          marginBottom: 4,
          borderLeft: `4px solid ${brandColors.primary}`,
          transition: 'transform 0.2s ease, box-shadow 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          '&:hover': {
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            transform: 'translateY(-2px)'
          },
          ...sx
        }}
      >
        <Typography 
          variant="h6" 
          sx={{ 
            color: brandColors.text,
            marginBottom: '20px', 
            fontWeight: 'bold',
            position: 'relative',
            paddingBottom: '8px',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              width: '40px',
              height: '3px',
              backgroundColor: brandColors.primary,
              borderRadius: '1px'
            }
          }}
        >
          {title}
        </Typography>
        {children}
      </Box>
    );
  };

  const handleNuevaInspeccion = () => {
    // Limpiar todo el localStorage
    localStorage.clear();
    
    // Llamar a la función de reinicio proporcionada por el componente padre
    if (onResetInspeccion) {
      onResetInspeccion();
    }
    
    // Redirigir al inicio
    history.push('/');
  };

  return (
    <Fade in={true}>
      <Container className="container" maxWidth="xl" style={{ backgroundColor: brandColors.background, minHeight: '100vh', padding: isMobile ? '14px' : '20px', position: 'relative' }}>
        {/* Elemento decorativo superior */}
        <Box 
          sx={{
            position: 'absolute',
            top: 0,
            right: 0,
            width: '80px',
            height: '80px',
            background: brandColors.accent,
            borderRadius: '0 0 0 100%',
            zIndex: 1
          }}
        />
        
        <Box textAlign="center" mb={4} sx={{ position: 'relative', zIndex: 2 }}>
          <Typography variant="h4" sx={{ 
            fontWeight: 'bold',
            color: brandColors.text,
            marginTop: '20px',
            position: 'relative',
            '&::after': {
              content: '""',
              position: 'absolute',
              bottom: '-8px',
              left: '50%',
              transform: 'translateX(-50%)',
              width: '60px',
              height: '4px',
              backgroundColor: brandColors.primary,
              borderRadius: '2px'
            }
          }}>
            Daños Detectados
          </Typography>
        </Box>
        {/* Elemento decorativo inferior */}
        <Box 
          sx={{
            position: 'absolute',
            bottom: 0,
            left: 0,
            width: '120px',
            height: '120px',
            background: `${brandColors.primary}33`,
            borderRadius: '0 100% 0 0',
            zIndex: 1
          }}
        />
        
        <Box
          mt={isMobile ? 2 : 4}
          mb={isMobile ? 2.5 : 4}
          sx={{
            position: 'relative',
            zIndex: 2,
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
            gap: 1.5
          }}
        >
          <Button 
            variant="contained" 
            onClick={onBack}
            sx={{ 
              width: '100%',
              backgroundColor: brandColors.secondary,
              color: brandColors.onSecondary,
              borderRadius: '50px',
              padding: '12px 24px',
              fontWeight: 'bold',
              boxShadow: '0 4px 8px rgba(0,0,0,0.1)',
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                backgroundColor: brandColors.primary,
                transform: 'translateY(-2px)',
                boxShadow: '0 6px 12px rgba(0,0,0,0.2)'
              }
            }}
          >
            Volver
          </Button>
          <Button 
            variant="contained" 
            onClick={handleNuevaInspeccion}
            sx={{ 
              width: '100%',
              backgroundColor: brandColors.primary,
              color: brandColors.onPrimary,
              borderRadius: '50px',
              padding: '12px 24px',
              fontWeight: 'bold',
              boxShadow: `0 4px 8px ${brandColors.primary}66`,
              transition: 'transform 0.3s ease, box-shadow 0.3s ease',
              '&:hover': {
                backgroundColor: brandColors.secondary,
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 12px ${brandColors.primary}88`
              }
            }}
          >
            Realizar Nueva Inspección
          </Button>
        </Box>
        <Box
          sx={{
            mt: isMobile ? 1 : 2.5,
            mb: isMobile ? 2 : 3,
            p: isMobile ? 1.4 : 1.8,
            borderRadius: 3,
            background: `linear-gradient(135deg, ${brandColors.primary} 0%, ${brandColors.secondary} 100%)`,
            color: '#fff',
            position: 'relative',
            zIndex: 2,
            boxShadow: '0 10px 24px rgba(0,0,0,0.12)'
          }}
        >
          <Typography sx={{ fontSize: isMobile ? '0.78rem' : '0.88rem', opacity: 0.86, fontWeight: 700, letterSpacing: 0.3 }}>
            Resumen por vistas
          </Typography>
          <Typography sx={{ mt: 0.5, fontSize: isMobile ? '1.1rem' : '1.45rem', fontWeight: 800, lineHeight: 1.1 }}>
            {Object.keys(danosPorFoto).length} vistas analizadas
          </Typography>
          <Typography sx={{ mt: 0.4, fontSize: isMobile ? '0.78rem' : '0.9rem', opacity: 0.9 }}>
            Cada vista muestra patente, marca, piezas afectadas y los daños asociados detectados por la API.
          </Typography>
        </Box>
        <Box 
          sx={{ 
            mt: isMobile ? 0.8 : 2,
            display: 'flex', 
            flexDirection: isMobile ? 'column' : 'row',
            overflowX: isMobile ? 'visible' : 'auto', 
            gap: isMobile ? 2 : 3, 
            pb: 2, 
            px: { xs: 1, md: 3 },
            snapType: isMobile ? 'none' : 'x mandatory',
            '&::-webkit-scrollbar': { height: '8px' },
            '&::-webkit-scrollbar-thumb': { backgroundColor: `${brandColors.primary}88`, borderRadius: '4px' },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' }
          }}
        >
          {Object.keys(danosPorFoto).map((fotoTipo, index) => (
            <Box key={index} sx={{ width: isMobile ? '100%' : { xs: '85vw', md: '450px' }, flexShrink: 0, scrollSnapAlign: 'center', display: 'flex' }}>
              <ResultBox title={fotoTipo} sx={{ flexGrow: 1, p: 2, mb: 0 }}>
                <Box display="flex" flexDirection="column" gap={2} sx={{ height: '100%' }}>
                  
                  {/* Datos como Patente o Marca */}
                  {(danosPorFoto[fotoTipo].patente || danosPorFoto[fotoTipo].marcaDetectada) && (
                    <Box>
                      {danosPorFoto[fotoTipo].patente && (
                      <Box mb={1}>
                        <Typography variant="subtitle2" color="textSecondary" style={{ marginBottom: '4px' }}>
                          Patente detectada:
                        </Typography>
                          <Box display="flex" alignItems={isMobile ? 'stretch' : 'center'} flexDirection={isMobile ? 'column' : 'row'} gap={1}>
                            <Typography variant="h6" sx={{ fontFamily: 'monospace', fontWeight: 'bold', wordBreak: 'break-word' }}>
                              {danosPorFoto[fotoTipo].patente}
                            </Typography>
                            <Button 
                              variant="outlined" 
                              size="small"
                              sx={{ ml: isMobile ? 0 : 2, alignSelf: isMobile ? 'flex-start' : 'center', fontSize: '10px', minWidth: 'auto', padding: '2px 8px' }}
                              onClick={() => navigator.clipboard.writeText(danosPorFoto[fotoTipo].patente)}
                            >
                              COPIAR
                            </Button>
                          </Box>
                        </Box>
                      )}
                      
                      {danosPorFoto[fotoTipo].marcaDetectada && (
                        <Box mb={1}>
                          <Typography variant="subtitle2" color="textSecondary" style={{ marginBottom: '4px' }}>
                            Marca detectada:
                          </Typography>
                          <Typography variant="body1" style={{ color: brandColors.primary, fontWeight: 'bold' }}>
                            {danosPorFoto[fotoTipo].marcaDetectada}
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  )}
                  
                  {/* Imagen del daño */}
                  {danosPorFoto[fotoTipo].visualizacion && (
                    <Box
                      textAlign="center"
                      sx={{
                        height: isMobile ? '150px' : '180px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        bgcolor: '#f5f5f5',
                        borderRadius: 2,
                        p: 1
                      }}
                    >
                      <img
                        src={formatVisualizationUrl(danosPorFoto[fotoTipo].visualizacion)}
                        alt={`Visualización de ${fotoTipo}`}
                        style={{
                          maxWidth: '100%',
                          maxHeight: '100%',
                          objectFit: 'contain',
                          cursor: 'pointer'
                        }}
                        onClick={() => setExpandedImage(formatVisualizationUrl(danosPorFoto[fotoTipo].visualizacion))}
                      />
                    </Box>
                  )}

                  {/* Detalles de piezas y daños */}
                  <Box sx={{ flexGrow: 1, overflowY: isMobile ? 'visible' : 'auto', pr: isMobile ? 0 : 1 }}>
                    {danosPorFoto[fotoTipo].partes && danosPorFoto[fotoTipo].partes.length > 0 ? (
                      <Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 1.2, flexWrap: 'wrap' }}>
                          <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>
                            Piezas afectadas
                          </Typography>
                          <Chip size="small" label={`${danosPorFoto[fotoTipo].partes.length} ${danosPorFoto[fotoTipo].partes.length === 1 ? 'pieza' : 'piezas'}`} sx={{ bgcolor: `${brandColors.primary}18`, color: brandColors.text, fontWeight: 700 }} />
                        </Box>
                        <Grid container spacing={1.2} mb={isMobile ? 2 : 3}>
                          {danosPorFoto[fotoTipo].partes.map((parte, parteIndex) => {
                            const parteColors = ['#1D766F', '#007bff', '#6610f2', '#fd7e14', '#e83e8c', '#6f42c1', '#20c997', '#17a2b8', '#28a745'];
                            const parteColor = parteColors[parteIndex % parteColors.length];
                            const parteName = (parte && parte.part && typeof parte.part.class === 'string')
                              ? (parte.part.class.endsWith('-parte') ? parte.part.class.replace('-parte', '') : parte.part.class)
                              : 'Parte no identificada';
                            const parteId = `P${parteIndex+1}`;
                            
                            return (
                              <Grid item xs={12} sm={6} key={parteIndex}>
                                <Paper elevation={1} style={{ 
                                  padding: '8px', 
                                  backgroundColor: 'white',
                                  borderLeft: `3px solid ${parteColor}`,
                                  borderRadius: '4px',
                                  display: 'flex',
                                  alignItems: 'center'
                                }}>
                                  <Box 
                                    display="flex" justifyContent="center" alignItems="center"
                                    style={{
                                      backgroundColor: parteColor, color: 'white', width: '24px', height: '24px',
                                      borderRadius: '50%', marginRight: '8px', fontWeight: 'bold', fontSize: '0.8rem'
                                    }}
                                  >
                                    {parteId}
                                  </Box>
                                  <Box flex={1}>
                                    <Typography variant="body2" style={{ fontWeight: 'bold', color: parteColor, lineHeight: 1.1 }}>
                                      {parteName}
                                    </Typography>
                                    {parte && parte.part && parte.part.confidence && (
                                      <Typography variant="caption" display="block" color="textSecondary" style={{ fontSize: '0.7rem' }}>
                                        Confianza: {Math.round(parte.part.confidence * 100)}%
                                      </Typography>
                                    )}
                                  </Box>
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                        
                        {danosPorFoto[fotoTipo].partes.map((parte, parteIndex) => {
                          if (!parte.associated_damages || parte.associated_damages.length === 0) return null;
                          const parteColors = ['#1D766F', '#007bff', '#6610f2', '#fd7e14', '#e83e8c', '#6f42c1', '#20c997', '#17a2b8', '#28a745'];
                          const parteColor = parteColors[parteIndex % parteColors.length];
                          const parteName = (parte && parte.part && typeof parte.part.class === 'string')
                            ? (parte.part.class.endsWith('-parte') ? parte.part.class.replace('-parte', '') : parte.part.class)
                            : 'Parte no identificada';
                          const parteId = `P${parteIndex + 1}`;
                          
                          return (
                            <Accordion key={parteIndex} disableGutters defaultExpanded={false} sx={{ mb: 1.2, borderRadius: '10px !important', overflow: 'hidden', boxShadow: 'none', border: `1px solid ${parteColor}40`, '&:before': { display: 'none' } }}>
                              <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ backgroundColor: `${parteColor}12`, minHeight: 52 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0 }}>
                                  <Box component="span" sx={{
                                    backgroundColor: parteColor, color: 'white', width: 22, height: 22,
                                    borderRadius: '50%', display: 'inline-flex', justifyContent: 'center', alignItems: 'center',
                                    mr: 1, fontWeight: 'bold', fontSize: '0.75rem', flexShrink: 0
                                  }}>
                                    {parteId}
                                  </Box>
                                  <Box sx={{ minWidth: 0 }}>
                                    <Typography variant="subtitle2" sx={{ color: parteColor, fontWeight: 800, lineHeight: 1.15 }}>
                                      {parteName}
                                    </Typography>
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                      {parte.associated_damages.length} {parte.associated_damages.length === 1 ? 'daño asociado' : 'daños asociados'}
                                    </Typography>
                                  </Box>
                                </Box>
                              </AccordionSummary>
                              <AccordionDetails sx={{ p: isMobile ? 1 : 1.25, bgcolor: '#fafafa' }}>
                                <Grid container spacing={1}>
                                  {parte.associated_damages.map((dano, danoIndex) => (
                                    <Grid item xs={12} sm={6} key={danoIndex}>
                                      <Paper elevation={0} style={{ 
                                        height: '100%', borderRadius: '8px', overflow: 'hidden', border: `1px solid ${parteColor}30`, backgroundColor: 'white'
                                      }}>
                                        <Box display="flex" flexDirection="column" height="100%">
                                          <Box display="flex" alignItems="center" p={1} style={{ backgroundColor: `${parteColor}12` }}>
                                            <Typography variant="body2" style={{ fontWeight: 'bold', color: parteColor, lineHeight: 1.2 }}>
                                              {dano.class}
                                            </Typography>
                                          </Box>
                                          <Box p={1} flex={1} bgcolor="#fff">
                                            {dano && dano.confidence && (
                                              <Typography variant="caption" display="block" color="textSecondary">
                                                Confianza: {Math.round(dano.confidence * 100)}%
                                              </Typography>
                                            )}
                                          </Box>
                                        </Box>
                                      </Paper>
                                    </Grid>
                                  ))}
                                </Grid>
                              </AccordionDetails>
                            </Accordion>
                          );
                        })}
                      </Box>
                    ) : (
                      <Typography variant="body2" color="textSecondary" sx={{ mt: 2, textAlign: 'center', fontStyle: 'italic' }}>
                        No se han detectado piezas con daños en esta vista.
                      </Typography>
                    )}
                  </Box>

                </Box>
              </ResultBox>
            </Box>
          ))}
          
          {Object.keys(danosPorFoto).length === 0 && (
            <Box sx={{ width: '100%', textAlign: 'center' }}>
              <Paper elevation={1} style={{ padding: '20px', textAlign: 'center', borderRadius: '10px' }}>
                <Typography variant="h6" color="textSecondary">
                  No se detectaron daños en el vehículo.
                </Typography>
              </Paper>
            </Box>
          )}
        </Box>
        {expandedImage && (
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              backgroundColor: 'rgba(0, 0, 0, 0.7)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
            onClick={() => setExpandedImage(null)}
          >
            <img
              src={expandedImage}
              alt="Expanded view"
              style={{
                maxWidth: '90%',
                maxHeight: '90%',
                objectFit: 'contain',
              }}
              onClick={(e) => e.stopPropagation()}
            />
          </Box>
        )}
      </Container>
    </Fade>
  );
};

export default DanosDetalle;
